import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import { Mail, Send, Loader2, CheckCircle, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TX = {
  bg: {
    title: "Свържете се с нас", desc: "Имате въпрос, предложение или искате да си сътрудничим? Ще се радваме да чуем от вас.",
    back: "Начало", email_title: "Email", email_desc: "Свържете се с нас чрез контактната форма",
    company_title: "Компания", name_label: "Име *", name_ph: "Вашето име",
    email_label: "Email *", email_ph: "вашият@email.com", subject_label: "Тема", subject_ph: "Относно...",
    msg_label: "Съобщение *", msg_ph: "Напишете съобщението си тук...", send: "Изпратете",
    thanks: "Благодарим ви!", thanks_desc: "Ще се свържем с вас скоро.", new_msg: "Ново съобщение",
    required: "Моля, попълнете всички задължителни полета", sent: "Съобщението е изпратено!", error: "Грешка при изпращане",
    seo_desc: "Свържете се с екипа на Nutramilo. Форма за контакт и информация.",
  },
  en: {
    title: "Contact Us", desc: "Have a question, suggestion, or want to collaborate? We'd love to hear from you.",
    back: "Home", email_title: "Email", email_desc: "Reach us through the contact form",
    company_title: "Company", name_label: "Name *", name_ph: "Your name",
    email_label: "Email *", email_ph: "your@email.com", subject_label: "Subject", subject_ph: "About...",
    msg_label: "Message *", msg_ph: "Write your message here...", send: "Send Message",
    thanks: "Thank you!", thanks_desc: "We'll get back to you soon.", new_msg: "New message",
    required: "Please fill in all required fields", sent: "Message sent!", error: "Error sending message",
    seo_desc: "Get in touch with the Nutramilo team.",
  },
  es: {
    title: "Contactenos", desc: "Tiene alguna pregunta, sugerencia o desea colaborar? Nos encantaria saber de usted.",
    back: "Inicio", email_title: "Email", email_desc: "Contactenos a traves del formulario",
    company_title: "Empresa", name_label: "Nombre *", name_ph: "Su nombre",
    email_label: "Email *", email_ph: "su@email.com", subject_label: "Asunto", subject_ph: "Acerca de...",
    msg_label: "Mensaje *", msg_ph: "Escriba su mensaje aqui...", send: "Enviar mensaje",
    thanks: "Gracias!", thanks_desc: "Nos pondremos en contacto pronto.", new_msg: "Nuevo mensaje",
    required: "Por favor complete todos los campos obligatorios", sent: "Mensaje enviado!", error: "Error al enviar",
    seo_desc: "Contacte al equipo de Nutramilo.",
  },
};

export default function ContactPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX[language === "bg" ? "bg" : "en"] || TX.en;

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t.sent);
    } catch {
      toast.error(t.error);
    } finally { setLoading(false); }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-background text-foreground' : 'bg-[#F9F9F7]'}`} data-testid="contact-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={t.title} description={t.seo_desc} path="/contact" />
      <SubpageNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <SubpageBackLink label={t.back} />

        <div className="text-center mb-12">
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.title}
          </h1>
          <p className={`text-sm sm:text-base max-w-xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.desc}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[hsl(223,47%,8%)] border-[hsl(223,47%,14%)]' : 'bg-white border-slate-100 shadow-sm'}`}>
              <Mail className="w-6 h-6 text-[#6366F1] mb-3" />
              <h3 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{t.email_title}</h3>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="contact-email">{t.email_desc}</p>
            </div>
            <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[hsl(223,47%,8%)] border-[hsl(223,47%,14%)]' : 'bg-white border-slate-100 shadow-sm'}`}>
              <MapPin className="w-6 h-6 text-[#10B981] mb-3" />
              <h3 className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{t.company_title}</h3>
              <div className="flex items-center gap-2">
                <img src="/sci-ink-press-logo.png" alt="Sci Ink Press" className="h-6 w-auto" onError={e => e.target.style.display = "none"} />
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>International Sci Ink Press Ltd.</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className={`lg:col-span-3 ${isDark ? 'bg-[hsl(223,47%,8%)] border-[hsl(223,47%,14%)]' : 'bg-white border-slate-100 shadow-sm'}`}>
            <CardContent className="p-6">
              {sent ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle className="w-14 h-14 mx-auto mb-4 text-[#10B981]" />
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{t.thanks}</h3>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.thanks_desc}</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}>
                    {t.new_msg}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className={`text-xs font-medium mb-1.5 block ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{t.name_label}</label>
                    <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t.name_ph}
                      className={isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white' : ''} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className={`text-xs font-medium mb-1.5 block ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{t.email_label}</label>
                    <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder={t.email_ph}
                      className={isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white' : ''} data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className={`text-xs font-medium mb-1.5 block ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{t.subject_label}</label>
                    <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder={t.subject_ph}
                      className={isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white' : ''} />
                  </div>
                  <div>
                    <label className={`text-xs font-medium mb-1.5 block ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{t.msg_label}</label>
                    <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder={t.msg_ph} rows={5}
                      className={isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white' : ''} data-testid="contact-message" />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#7B1FA2] to-[#F06292] hover:from-[#6A1B9A] hover:to-[#E91E63] text-white h-11 rounded-xl" disabled={loading} data-testid="contact-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    {t.send}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <SubpageFooter />
    </div>
  );
}
